import { z } from 'zod';
import { esquemaMotivo } from './comunes.js';
import { esquemaSlug } from './organizacion.js';
/**
 * Accesos de un consultorio (ADR-015): slug, subdominio y dominio propio.
 *
 * - **Slug**: identificador legible de las rutas publicas (`/acceso/<slug>`,
 *   `/agenda/<slug>`). No es la llave de ninguna relacion: todo se relaciona por
 *   `organizacionId`, asi que cambiarlo no mueve datos.
 * - **Subdominio**: nombre bajo el dominio base que configura el despliegue
 *   (`DOMINIO_BASE_CONSULTORIOS`). Sin dominio base no hay subdominios.
 * - **Dominio propio**: un hostname externo que el consultorio apunta al
 *   servidor. Guardarlo no configura DNS, proxy ni certificado.
 *
 * Un valor que alguna vez uso un consultorio queda suyo: otro no puede tomarlo y
 * las direcciones viejas siguen llevando al mismo consultorio.
 */
export const TIPOS_DE_ACCESO = ['slug', 'subdominio', 'dominio'];
/**
 * Nombres que no puede tomar un consultorio: rutas de la aplicacion, nombres de
 * infraestructura y los que se confunden con la plataforma. Aplica a slugs y
 * subdominios nuevos; uno ya existente no se invalida.
 */
export const NOMBRES_RESERVADOS = [
    'acceso',
    'admin',
    'administracion',
    'agenda',
    'ayuda',
    'api',
    'app',
    'assets',
    'blog',
    'cdn',
    'consola',
    'correo',
    'demo',
    'dev',
    'docs',
    'ftp',
    'imap',
    'invitacion',
    'localhost',
    'mail',
    'ns1',
    'ns2',
    'plataforma',
    'pop',
    'portal',
    'qa',
    'registro',
    'restablecer',
    'root',
    'smtp',
    'soporte',
    'staging',
    'static',
    'status',
    'test',
    'webmail',
    'www',
];
export function esNombreReservado(valor) {
    return NOMBRES_RESERVADOS.includes(valor.trim().toLowerCase());
}
/**
 * Sugerencia de slug a partir del nombre del consultorio: minusculas, sin
 * acentos, con guiones y de 40 caracteres como maximo (lo que pide `esquemaSlug`).
 */
export function sugerirSlug(texto) {
    return texto
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 40)
        .replace(/-+$/g, '');
}
/** Slug para un consultorio nuevo o un cambio: el de siempre, menos los reservados. */
export const esquemaSlugNuevo = esquemaSlug.refine((valor) => !esNombreReservado(valor), {
    message: 'Ese nombre está reservado',
});
/** Una etiqueta de DNS bajo el dominio base: mismas reglas que el slug. */
export const esquemaSubdominio = esquemaSlugNuevo;
/**
 * Normaliza lo que alguien escribe como dominio: sin esquema, ruta, puerto ni
 * punto final, y en minusculas. `https://Citas.Ejemplo.mx/` → `citas.ejemplo.mx`.
 */
export function normalizarHost(valor) {
    return valor
        .trim()
        .toLowerCase()
        .replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
        .replace(/[/?#].*$/, '')
        .replace(/:\d+$/, '')
        .replace(/\.$/, '');
}
const ETIQUETA_DNS = /^(?!-)[a-z0-9-]{1,63}(?<!-)$/;
/**
 * Hostname de un dominio propio. ASCII (un dominio con acentos se escribe en
 * punycode, `xn--...`), al menos dos etiquetas, TLD alfabetico y nunca una IP.
 */
export const esquemaHost = z
    .string()
    .transform(normalizarHost)
    .pipe(z
    .string()
    .min(4, 'Escriba el dominio completo, p. ej. citas.ejemplo.mx')
    .max(253, 'El dominio es demasiado largo')
    .refine((host) => {
    const etiquetas = host.split('.');
    if (etiquetas.length < 2)
        return false;
    if (!etiquetas.every((e) => ETIQUETA_DNS.test(e)))
        return false;
    return /^(?:[a-z]{2,63}|xn--[a-z0-9-]{1,59})$/.test(etiquetas.at(-1));
}, { message: 'No es un dominio válido. Use letras, números, guiones y puntos.' }));
/**
 * Estado de un subdominio o dominio propio, segun la ultima comprobacion REAL
 * (DNS y certificado). `activo` solo se pone con evidencia: el nombre resolvio
 * al servidor y respondio la aplicacion por HTTPS con un certificado valido.
 */
export const ESTADOS_DE_DOMINIO = [
    'sin-configurar',
    'pendiente-dns',
    'pendiente-certificado',
    'activo',
    'error',
];
export const esquemaCambioDeSlug = z
    .object({ slug: esquemaSlugNuevo, motivo: esquemaMotivo })
    .strict();
export const esquemaCambioDeSubdominio = z
    .object({ subdominio: esquemaSubdominio.nullable(), motivo: esquemaMotivo })
    .strict();
export const esquemaCambioDeDominio = z
    .object({ host: esquemaHost.nullable(), motivo: esquemaMotivo })
    .strict();
export const esquemaConsultaDeDisponibilidad = z.object({
    tipo: z.enum(TIPOS_DE_ACCESO),
    valor: z.string().trim().min(1).max(253),
    /** Al editar: el valor que ya es de este consultorio no cuenta como ocupado. */
    organizacionId: z
        .string()
        .regex(/^[a-f0-9]{24}$/i)
        .optional(),
});
//# sourceMappingURL=accesos.js.map